import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, DollarSign, AlertTriangle, Plus } from 'lucide-react';
import { clientesService } from '../services/api';
import { formatCurrency, formatDate, formatCPFCNPJ, formatPhone } from '../utils/formatters';

const ClienteDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: cliente, isLoading, error } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => clientesService.detalhes(id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Erro ao carregar dados do cliente</p>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-600">Cliente não encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/clientes')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{cliente.nome_cliente}</h1>
            <p className="text-gray-600">ID: {cliente.id_cliente}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button className="btn-primary">Editar Cliente</button>
          <button 
            onClick={() => navigate(`/controle-cobranca/novo?cliente=${cliente.id}`)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Plus size={16} />
            <span>Nova Cobrança</span>
          </button>
        </div>
      </div>

      {/* Informações do Cliente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dados Pessoais */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Pessoais</h2>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Mail className="text-gray-400" size={16} />
              <span className="text-gray-600">{cliente.email || 'Não informado'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="text-gray-400" size={16} />
              <span className="text-gray-600">{formatPhone(cliente.telefone) || 'Não informado'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="text-gray-400" size={16} />
              <span className="text-gray-600">{cliente.endereco || 'Não informado'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="text-gray-400" size={16} />
              <span className="text-gray-600">
                Cadastrado em: {formatDate(cliente.data_cadastro)}
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400 font-medium">CPF/CNPJ:</span>
              <span className="text-gray-600">{formatCPFCNPJ(cliente.cpf_cnpj) || 'Não informado'}</span>
            </div>
          </div>
        </div>

        {/* Resumo Financeiro */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo Financeiro</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-blue-700 font-medium">Total em Aberto</span>
              <span className="text-blue-700 font-bold">{formatCurrency(cliente.total_valor_aberto)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-100 rounded-lg">
              <span className="text-red-700 font-medium">Valor em Atraso</span>
              <span className="text-red-700 font-bold">{formatCurrency(cliente.valor_em_atraso)}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
              <span className="text-orange-700 font-medium">Contas Abertas</span>
              <span className="text-orange-700 font-bold">{cliente.total_contas_abertas}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
              <span className="text-red-700 font-medium">Contas Vencidas</span>
              <span className="text-red-700 font-bold">{cliente.contas_vencidas}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-yellow-700 font-medium">Vencendo em 7 dias</span>
              <span className="text-yellow-700 font-bold">{cliente.contas_vencendo_em_7_dias}</span>
            </div>
          </div>
        </div>

        {/* Histórico de Pagamentos */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Histórico de Pagamentos</h2>
          
          {/* Resumo Geral */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Taxa de Pagamento em Dia</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cliente.percentual_pagamento_em_dia}%
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {cliente.total_contas_pagas_em_dia} de {cliente.total_contas_pagas} contas pagas em dia
                </p>
              </div>
            </div>
          </div>

          {/* Estatísticas Detalhadas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <span className="text-green-700 dark:text-green-300 font-medium">Contas Pagas em Dia</span>
                <span className="text-green-700 dark:text-green-300 font-bold">{cliente.total_contas_pagas_em_dia}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-blue-700 dark:text-blue-300 font-medium">Contas Pagas com Atraso</span>
                <span className="text-blue-700 dark:text-blue-300 font-bold">
                  {cliente.total_contas_pagas - cliente.total_contas_pagas_em_dia}
                </span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <span className="text-red-700 dark:text-red-300 font-medium">Contas Vencidas Não Pagas</span>
                <span className="text-red-700 dark:text-red-300 font-bold">{cliente.total_contas_nao_pagas_em_dia}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <span className="text-purple-700 dark:text-purple-300 font-medium">Total de Contas Pagas</span>
                <span className="text-purple-700 dark:text-purple-300 font-bold">{cliente.total_contas_pagas}</span>
              </div>
            </div>
          </div>

          {/* Legenda */}
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Como é calculado:</strong> Consideramos todas as contas vencidas do cliente. 
              Contas pagas antes ou no dia do vencimento são consideradas "em dia". 
              Contas vencidas e não pagas são consideradas "não pagas em dia".
            </p>
          </div>
        </div>
      </div>

      {/* Contas do Cliente */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Contas a Receber</h2>
          <button className="btn-primary">Sincronizar</button>
        </div>
        
        {cliente.contas && cliente.contas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Conta</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Vencimento</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Valor</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Pago</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {cliente.contas.map((conta) => (
                  <tr key={conta.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{conta.nome_conta}</p>
                        <p className="text-sm text-gray-500">#{conta.id_conta_rec}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={conta.esta_vencido ? 'text-red-600' : 'text-gray-900'}>
                        {formatDate(conta.vencimento_rec)}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium">{formatCurrency(conta.valor_rec)}</td>
                    <td className="py-3 px-4">{formatCurrency(conta.valor_pago)}</td>
                    <td className="py-3 px-4">
                      <span className={`badge ${conta.esta_vencido ? 'badge-danger' : 'badge-success'}`}>
                        {conta.status_vencimento}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Ver Detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="mx-auto text-gray-400 mb-2" size={48} />
            <p className="text-gray-500">Nenhuma conta encontrada para este cliente</p>
          </div>
        )}
      </div>

      {/* Histórico de Cobranças */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Histórico de Cobranças</h2>
        {cliente.ultima_cobranca ? (
          <div className="space-y-3">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-gray-900">
                    Última cobrança: {formatDate(cliente.ultima_cobranca.data_cobranca)}
                  </p>
                  <p className="text-sm text-gray-600">{cliente.ultima_cobranca.observacoes}</p>
                </div>
                <span className={`badge ${cliente.ultima_cobranca.status === 'Cobrado' ? 'badge-success' : 'badge-warning'}`}>
                  {cliente.ultima_cobranca.status}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum histórico de cobrança encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClienteDetalhes; 